
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Component, ComponentType } from "@/utils/componentService";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { extractRepoNameFromUrl } from "@/utils/repositoryUtils";

interface EditComponentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  component: Component | null;
  onSave: (name: string, componentTypeId: string, repoType: string, repoName: string, repoUrl: string, mainUrl: string, logsUrl: string, dashUrl: string, swaggerUrl: string) => void;
  componentTypes: ComponentType[];
  isLoading: boolean;
}

export function EditComponentDialog({ 
  open, 
  onOpenChange, 
  component, 
  onSave,
  componentTypes,
  isLoading
}: EditComponentDialogProps) {
  const [name, setName] = useState("");
  const [componentTypeId, setComponentTypeId] = useState("");
  const [repoType, setRepoType] = useState("");
  const [repoName, setRepoName] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [mainUrl, setMainUrl] = useState("");
  const [logsUrl, setLogsUrl] = useState("");
  const [dashUrl, setDashUrl] = useState("");
  const [swaggerUrl, setSwaggerUrl] = useState("");
  const [nameError, setNameError] = useState("");
  const [typeError, setTypeError] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  
  useEffect(() => {
    if (component && open) {
      setName(component.name);
      setComponentTypeId(component.id_component_type);
      setRepoType(component.repo_type || "");
      setRepoName(component.repo_name || "");
      setRepoUrl(component.repo_url || "");
      setMainUrl(component.main_url || "");
      setLogsUrl(component.logs_url || "");
      setDashUrl(component.dash_url || "");
      setSwaggerUrl(component.swagger_url || "");
      setNameError("");
      setTypeError("");
    }
    setInternalOpen(open);
  }, [component, open]);

  // Extrai automaticamente o nome do repositório quando a URL muda
  useEffect(() => {
    const extractedName = extractRepoNameFromUrl(repoUrl);
    setRepoName(extractedName);
  }, [repoUrl]);
  
  // Sort component types alphabetically by group + name
  const sortedComponentTypes = [...componentTypes].sort((a, b) => {
    const textA = `${a.group_name || ''} ${a.name}`.trim();
    const textB = `${b.group_name || ''} ${b.name}`.trim();
    return textA.localeCompare(textB);
  });
  
  const validateForm = () => {
    let valid = true;
    
    if (!name.trim()) {
      setNameError("O nome é obrigatório");
      valid = false;
    } else {
      setNameError("");
    }
    
    if (!componentTypeId) {
      setTypeError("O tipo é obrigatório");
      valid = false;
    } else {
      setTypeError("");
    }
    
    return valid;
  };
  
  const handleSubmit = () => {
    if (validateForm()) {
      onSave(name, componentTypeId, repoType, repoName, repoUrl, mainUrl, logsUrl, dashUrl, swaggerUrl);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (isLoading) return;
    
    setInternalOpen(newOpen);
    if (!newOpen) {
      setTimeout(() => {
        onOpenChange(false);
      }, 100);
    } else {
      onOpenChange(true);
    }
  };

  return (
    <Dialog open={internalOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar Componente</DialogTitle>
          <DialogDescription>
            Atualize os dados do componente.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="dados-basicos" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
            <TabsTrigger value="repositorio">Repositório</TabsTrigger>
            <TabsTrigger value="urls">URLs</TabsTrigger>
          </TabsList>
          
          <TabsContent value="dados-basicos" className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-type">Tipo de Componente</Label>
              <Select 
                value={componentTypeId} 
                onValueChange={setComponentTypeId}
                disabled={isLoading}
              >
                <SelectTrigger id="edit-type">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {sortedComponentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.group_name ? `${type.group_name} - ${type.name}` : type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {typeError && <p className="text-sm text-destructive">{typeError}</p>}
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Nome do Componente</Label>
              <Input 
                id="edit-name" 
                value={name} 
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome do componente"
                disabled={isLoading}
              />
              {nameError && <p className="text-sm text-destructive">{nameError}</p>}
            </div>
          </TabsContent>

          <TabsContent value="repositorio" className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-repoType">Tipo de Repositório</Label>
              <Select 
                value={repoType} 
                onValueChange={setRepoType}
                disabled={isLoading}
              >
                <SelectTrigger id="edit-repoType">
                  <SelectValue placeholder="Selecione o tipo de repositório" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GitHub">GitHub</SelectItem>
                  <SelectItem value="Bitbucket">Bitbucket</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-repoUrl">URL Repositório</Label>
              <Input 
                id="edit-repoUrl" 
                value={repoUrl} 
                onChange={(e) => setRepoUrl(e.target.value)}
                placeholder="https://github.com/usuario/repositorio"
                disabled={isLoading}
              />
              {repoName && (
                <p className="text-xs text-muted-foreground">
                  Nome extraído: {repoName}
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="urls" className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-mainUrl">URL do Componente</Label>
              <Input 
                id="edit-mainUrl" 
                value={mainUrl} 
                onChange={(e) => setMainUrl(e.target.value)}
                placeholder="https://exemplo.com"
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-logsUrl">URL Índice de Logs</Label>
              <Input 
                id="edit-logsUrl" 
                value={logsUrl} 
                onChange={(e) => setLogsUrl(e.target.value)}
                placeholder="https://logs.exemplo.com"
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-dashUrl">URL Dashboard</Label>
              <Input 
                id="edit-dashUrl" 
                value={dashUrl} 
                onChange={(e) => setDashUrl(e.target.value)}
                placeholder="https://dashboard.exemplo.com"
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-swaggerUrl">URL Swagger</Label>
              <Input 
                id="edit-swaggerUrl" 
                value={swaggerUrl} 
                onChange={(e) => setSwaggerUrl(e.target.value)}
                placeholder="https://api.exemplo.com/swagger"
                disabled={isLoading}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-[#9b87f5] hover:bg-[#9b87f5]/90"
          >
            {isLoading ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
